// Dónde acomodarlo · I5
//
// Para la recepción y la terminal (las monta el integrador): al recibir
// un producto, la ubicación que mejor puntúa con su porqué y hasta dos
// alternativas. Aceptar o rechazar con motivo; el rechazo afina la regla
// de la empresa. Quien no tiene el permiso no ve un botón muerto: ve
// por qué no.
import { useCallback, useEffect, useState } from 'react';
import { MapPin, Check, X } from 'lucide-react';
import { api } from '../api';
import Ayuda from '../ui/Ayuda';
import { ChipSintetico } from './DemandaYReorden';

export default function DondeAcomodarlo({ idProducto, cantidad, onAceptada }) {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [decidida, setDecidida] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [rechazando, setRechazando] = useState(false);
  const [ocupado, setOcupado] = useState(false);

  const cargar = useCallback(() => {
    if (!idProducto) return;
    const q = cantidad ? `&cantidad=${encodeURIComponent(cantidad)}` : '';
    api.get(`/inteligencia/ubicacion-sugerida?id_producto=${idProducto}${q}`)
      .then((d) => { setDatos(d); setDecidida(null); setError(null); setRechazando(false); setMotivo(''); })
      .catch((e) => setError(e.status === 403
        ? 'Tu rol no decide ubicaciones. Pide a un operador o encargado que lo acomode.'
        : (e.message || 'No se pudo calcular la ubicación sugerida.')));
  }, [idProducto, cantidad]);
  useEffect(() => { cargar(); }, [cargar]);

  async function decidir(decision) {
    if (!datos?.id_sugerencia) return;
    setOcupado(true); setError(null);
    try {
      const r = await api.post(`/inteligencia/ubicacion-sugerida/${datos.id_sugerencia}/decidir`, {
        decision, motivo: decision === 'RECHAZADA' ? motivo : null,
      });
      setDecidida(r);
      if (decision === 'ACEPTADA' && onAceptada) onAceptada(datos.sugerencias[0]);
    } catch (e) { setError(e.message); }
    finally { setOcupado(false); }
  }

  if (error) return <p role="alert" className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-sm text-amber-800">{error}</p>;
  if (!datos) return <p className="text-sm text-gray-500">Buscando dónde acomodarlo…</p>;

  if (datos.estado !== 'OK') {
    return (
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
        <p className="font-semibold">Sin ubicación sugerida.</p>
        <p>{datos.mensaje}</p>
      </div>
    );
  }
  const [mejor, ...otras] = datos.sugerencias;
  const motivos = Object.entries(datos.motivos_rechazo || {});

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex flex-wrap items-center gap-2">
        <MapPin className="h-4 w-4 text-gray-500" />
        <h3 className="font-semibold text-gray-900">Dónde acomodarlo</h3>
        {datos.producto?.estado === 'CUARENTENA' && <span className="rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700">En cuarentena</span>}
      </div>
      <Ayuda id="inteligencia.ubicacion" />
      <div className="mt-3 rounded-lg border border-[var(--color-marca-borde)] bg-[var(--color-marca-suave)] p-3">
        <p className="text-lg font-semibold text-gray-900">{mejor.codigo} <span className="text-sm font-normal text-gray-600">· {mejor.nombre}</span></p>
        <p className="text-sm text-gray-700">{mejor.razon}</p>
        {datos.sin_volumetria && (
          <p className="mt-1 text-xs text-gray-600">Este producto no tiene peso registrado: la regla de pesados no se aplicó. Se captura en la ficha del producto.</p>
        )}
      </div>
      {otras.length > 0 && (
        <div className="mt-2 text-xs text-gray-600">
          <p className="font-semibold">También podría ir en:</p>
          <ul className="list-disc pl-5">{otras.map((s) => <li key={s.id_ubicacion}>{s.razon}</li>)}</ul>
        </div>
      )}
      {decidida ? (
        <p className="mt-3 rounded-lg border border-green-200 bg-green-50 p-2 text-sm text-green-800">
          {decidida.decision === 'ACEPTADA'
            ? `Aceptada: ${mejor.codigo}. Ya quedó registrado.`
            : `Rechazada (${datos.motivos_rechazo?.[decidida.motivo] || decidida.motivo}). Esa regla pesará menos la próxima vez.`}
        </p>
      ) : rechazando ? (
        <div className="mt-3 space-y-2">
          <label className="block text-sm text-gray-700">
            ¿Por qué no va ahí? El motivo es lo que afina la regla.
            <select value={motivo} onChange={(e) => setMotivo(e.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 p-2 text-sm">
              <option value="">Elige un motivo</option>
              {motivos.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          <div className="flex gap-2">
            <button type="button" disabled={!motivo || ocupado} onClick={() => decidir('RECHAZADA')}
              className="rounded-lg bg-gray-900 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50">
              Confirmar rechazo
            </button>
            <button type="button" onClick={() => setRechazando(false)} className="rounded-lg border border-gray-200 px-3 py-2 text-sm">Volver</button>
          </div>
          {!motivo && <p className="text-xs text-gray-500">El botón se activa al elegir un motivo.</p>}
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" disabled={ocupado} onClick={() => decidir('ACEPTADA')}
            className="inline-flex items-center gap-1 rounded-lg bg-[var(--color-marca)] px-3 py-2 text-sm font-semibold text-white disabled:opacity-50">
            <Check className="h-4 w-4" /> Sí, lo pongo ahí
          </button>
          <button type="button" disabled={ocupado} onClick={() => setRechazando(true)}
            className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-sm disabled:opacity-50">
            <X className="h-4 w-4" /> No, va en otro lado
          </button>
        </div>
      )}
      {datos.sugerencias.some((s) => s.reglas?.sintetico) && <div className="mt-2"><ChipSintetico /></div>}
    </div>
  );
}
